import { NotFound } from '@/Error';
import { useGetAccountQuery } from '@/services/accounts';
import { Alert, Box, Button, TextInput } from '@palmetto/palmetto-components';
import { Form, Formik, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { isErrorWithData } from '../../services/helpers';
import { MainContainer } from '../MainContainer';

import { Adders } from './Adders';
import { EstimatedEscalationRates } from './EscalationRates';
import { EstimatedPricingTable } from './EstimatedPricingTable';
import { useGetAccountEstimatedPricing } from './hooks/useGetAccountEstimatedPricing';

const pricingCalculatorSchema = yup.object().shape({
  systemSizeKw: yup.number().required('System size is required').min(0.1, 'System size must be greater than 0'),
  systemFirstYearProductionKwh: yup
    .number()
    .required('System production is required')
    .min(1, 'System production must be greater than 0'),
});

const PricingCalculatorForm = ({ isAccountLoading }: { isAccountLoading: boolean }) => {
  const { values, errors, touched, handleChange, handleBlur, isValid } = useFormikContext<{
    systemSizeKw?: number;
    systemFirstYearProductionKwh?: number;
  }>();
  const pricingData = useGetAccountEstimatedPricing(values);
  const { refetchPricing, error: pricingError, isLoading: pricingIsLoading, pricingFetched } = pricingData;

  const handleFetchPricing = () => {
    if (values.systemSizeKw && values.systemFirstYearProductionKwh) {
      refetchPricing();
    }
  };

  return (
    <Form>
      {!isAccountLoading && pricingError && (
        <Box childGap="md">
          <Alert variant="warning" message={isErrorWithData(pricingError) && pricingError.data.message} />
        </Box>
      )}
      <Box radius="md" borderWidth="xs" borderColor="separator" direction="column" padding="lg" childGap="md">
        <Box fontSize="lg" fontWeight="medium">
          Solar Energy System
        </Box>
        <Box direction={{ base: 'column', tablet: 'row' }} childGap="md" alignItems="flex-end">
          <Box>
            <TextInput
              id="systemSizeKw"
              name="systemSizeKw"
              label="System Size (kW)"
              type="number"
              step="0.01"
              value={values.systemSizeKw || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.systemSizeKw && errors.systemSizeKw ? errors.systemSizeKw : undefined}
              isRequired
            />
          </Box>
          <Box>
            <TextInput
              id="systemFirstYearProductionKwh"
              name="systemFirstYearProductionKwh"
              label="Year-1 Est. Production (kWh)"
              type="number"
              step="1"
              value={values.systemFirstYearProductionKwh || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.systemFirstYearProductionKwh && errors.systemFirstYearProductionKwh
                  ? errors.systemFirstYearProductionKwh
                  : undefined
              }
              isRequired
            />
          </Box>
          <Button
            variant="primary"
            onClick={handleFetchPricing}
            isDisabled={!isValid || pricingIsLoading}
            isLoading={pricingIsLoading}
          >
            Fetch Pricing
          </Button>
        </Box>
      </Box>
      {pricingFetched && (
        <>
          <EstimatedEscalationRates escalationRates={pricingData.escalationRates} />
          <Adders />
          <EstimatedPricingTable pricingData={pricingData} />
        </>
      )}
    </Form>
  );
};

export const SolarPricingCalculator = () => {
  const { id } = useParams<{ id: any }>();
  const { data: accountData, isLoading: accountIsLoading } = useGetAccountQuery(id);
  const { solarPricingCalculator = false } = useFlags();
  if (!solarPricingCalculator) {
    return <NotFound />;
  }
  const initialValues: {
    systemSizeKw?: number;
    systemFirstYearProductionKwh?: number;
  } = {
    systemSizeKw: accountData?.systemDesign?.systemSizeKw,
    systemFirstYearProductionKwh: accountData?.systemDesign?.systemFirstYearProductionKwh,
  };

  return (
    <>
      <Helmet>
        <title>Payment Calculator</title>
      </Helmet>
      <MainContainer>
        <Box childGap="lg" padding={{ base: '0 lg', desktop: '0' }}>
          <Formik
            initialValues={initialValues}
            onSubmit={() => {}}
            enableReinitialize={true}
            validationSchema={pricingCalculatorSchema}
            validateOnChange={true}
          >
            <PricingCalculatorForm isAccountLoading={accountIsLoading} />
          </Formik>
        </Box>
      </MainContainer>
    </>
  );
};
